'use client';
import { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { useRouter } from 'next/navigation';
import { contacts } from '@/_config';
import { OrderTicketInfoPanel } from './OrderTicketInfoPanel';

const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

const getDisplayName = (user) => {
    if (user?.firstName) return user.firstName;
    if (user?.name && user.name !== user.email) return user.name.split(' ')[0];
    return 'there';
};

const getInitials = (user) => {
    const first = user?.firstName?.[0] || user?.name?.[0];
    const last  = user?.lastName?.[0];
    if (first && last) return (first + last).toUpperCase();
    if (first) return first.toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return 'U';
};

// ── Main hero ─────────────────────────────────────────────────────────────────
export const SupportHero = () => {
    const router = useRouter();
    const [authUser,  setAuthUser]  = useState(null);
    const [mounted,   setMounted]   = useState(false);
    const [bgVisible, setBgVisible] = useState(true);

    useEffect(() => {
        setAuthUser(getAuthUser());
        setMounted(true);
    }, []);

    useEffect(() => {
        // Hide all bg icons after entrance animations finish (~3s)
        const t1 = setTimeout(() => setBgVisible(false), 3000);
        // Show all bg icons together after 2s of being hidden (~5s total)
        const t2 = setTimeout(() => setBgVisible(true), 5000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    const isLoggedIn = !!authUser;

    return (
        <section style={{
            background: 'var(--primary-color)',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <style>{`
                @keyframes sh-pulse {
                    0%   { transform: scale(1);   opacity: 0.45; }
                    100% { transform: scale(2.4); opacity: 0; }
                }
                @keyframes sh-drift {
                    0%   { transform: translateX(0)   translateY(0); }
                    33%  { transform: translateX(8px) translateY(-12px); }
                    66%  { transform: translateX(-6px) translateY(6px); }
                    100% { transform: translateX(0)   translateY(0); }
                }
                @keyframes sh-welcome-in {
                    0%   { opacity: 0; transform: translateY(-14px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                @keyframes sh-avatar-glow {
                    0%, 100% { box-shadow: 0 0 0 3px rgba(27,184,167,0.28), 0 0 0 6px rgba(27,184,167,0.1), 0 0 18px rgba(27,184,167,0.25); }
                    50%      { box-shadow: 0 0 0 4px rgba(27,184,167,0.45), 0 0 0 9px rgba(27,184,167,0.18), 0 0 30px rgba(27,184,167,0.4); }
                }
                @keyframes sh-border-glow {
                    0%, 100% { border-color: rgba(27,184,167,0.32); box-shadow: 0 0 0 0 rgba(27,184,167,0); }
                    50%      { border-color: rgba(27,184,167,0.6);  box-shadow: 0 0 16px rgba(27,184,167,0.2); }
                }
                @keyframes sh-wave {
                    0%, 60%, 100% { transform: rotate(0deg); }
                    10%, 30%      { transform: rotate(18deg); }
                    20%           { transform: rotate(-10deg); }
                }
                .sh-welcome-banner {
                    animation: sh-welcome-in 0.5s cubic-bezier(0.22,1,0.36,1) both, sh-border-glow 3s ease-in-out 0.6s infinite;
                }
                .sh-avatar-circle {
                    animation: sh-avatar-glow 3s ease-in-out infinite;
                }
                .sh-wave-emoji {
                    display: inline-block;
                    animation: sh-wave 2.2s ease-in-out 0.8s 2;
                    transform-origin: 70% 80%;
                }
                /* ── one-time background icon entrance animations ── */
                @keyframes sh-bg-fadein {
                    0%   { opacity: 0; transform: scale(0.5); }
                    60%  { opacity: 0.14; transform: scale(1.1); }
                    100% { opacity: 0.08; transform: scale(1); }
                }
                @keyframes sh-bg-slide-left {
                    0%   { opacity: 0; transform: translateX(-20px) rotate(-15deg); }
                    70%  { opacity: 0.13; transform: translateX(3px) rotate(4deg); }
                    100% { opacity: 0.07; transform: translateX(0) rotate(0deg); }
                }
                @keyframes sh-bg-slide-right {
                    0%   { opacity: 0; transform: translateX(20px) rotate(12deg); }
                    70%  { opacity: 0.12; transform: translateX(-3px) rotate(-3deg); }
                    100% { opacity: 0.07; transform: translateX(0) rotate(0deg); }
                }
                @keyframes sh-bg-drop {
                    0%   { opacity: 0; transform: translateY(-18px) scale(0.7); }
                    65%  { opacity: 0.13; transform: translateY(4px) scale(1.05); }
                    100% { opacity: 0.08; transform: translateY(0) scale(1); }
                }
                @keyframes sh-bg-rise {
                    0%   { opacity: 0; transform: translateY(18px) rotate(10deg); }
                    65%  { opacity: 0.12; transform: translateY(-3px) rotate(-2deg); }
                    100% { opacity: 0.07; transform: translateY(0) rotate(0deg); }
                }
                .sh-float-icon { position: absolute; pointer-events: none; }
                .sh-pulse-ring {
                    position: absolute; inset: -12px; border-radius: 50%;
                    border: 1.5px solid rgba(94,234,212,0.35);
                    animation: sh-pulse 2.4s ease-out infinite;
                    pointer-events: none;
                }
                .sh-pulse-ring-2 {
                    position: absolute; inset: -24px; border-radius: 50%;
                    border: 1px solid rgba(94,234,212,0.2);
                    animation: sh-pulse 2.4s ease-out 0.8s infinite;
                    pointer-events: none;
                }
            `}</style>

            {/* ── Background icons: animate in once, hide, then reappear together ── */}
            <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                opacity: bgVisible ? 1 : 0,
                transition: bgVisible ? 'opacity 1s ease' : 'opacity 0.5s ease',
            }}>
                {[
                    { icon: 'pi pi-headphones',      top: '10%',  left: '5%',   size: '1rem',   anim: 'sh-bg-slide-left  0.9s ease-out 0.2s forwards' },
                    { icon: 'pi pi-comments',        top: '15%',  right: '7%',  size: '0.9rem', anim: 'sh-bg-slide-right 0.9s ease-out 0.5s forwards' },
                    { icon: 'pi pi-shield',          top: '58%',  left: '3%',   size: '1.1rem', anim: 'sh-bg-rise        1s   ease-out 0.8s forwards'  },
                    { icon: 'pi pi-phone',           top: '68%',  right: '5%',  size: '0.85rem',anim: 'sh-bg-rise        1s   ease-out 1.1s forwards'  },
                    { icon: 'pi pi-ticket',          top: '33%',  left: '2%',   size: '0.8rem', anim: 'sh-bg-fadein      0.8s ease-out 1.4s forwards'  },
                    { icon: 'pi pi-check-circle',    top: '48%',  right: '3%',  size: '0.9rem', anim: 'sh-bg-slide-right 0.9s ease-out 0.3s forwards' },
                    { icon: 'pi pi-question-circle', top: '78%',  left: '14%',  size: '0.85rem',anim: 'sh-bg-drop        0.9s ease-out 1.6s forwards'  },
                    { icon: 'pi pi-envelope',        top: '22%',  right: '13%', size: '0.8rem', anim: 'sh-bg-drop        0.8s ease-out 0.9s forwards'  },
                ].map(({ icon, top, left, right, size, anim }) => (
                    <div key={icon + top} className="sh-float-icon" style={{ top, left, right, opacity: 0, animation: anim }}>
                        <i className={icon} style={{ fontSize: size, color: 'rgba(255,255,255,0.18)' }} />
                    </div>
                ))}
            </div>

            {/* Dot grid */}
            <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
            }} />

            {/* Soft glow orbs */}
            <div style={{
                position: 'absolute', top: '-4rem', right: '-4rem',
                width: '22rem', height: '22rem', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(27,184,167,0.18) 0%, transparent 70%)',
                pointerEvents: 'none', animation: 'sh-drift 12s ease-in-out infinite',
            }} />
            <div style={{
                position: 'absolute', bottom: '-3rem', left: '-3rem',
                width: '18rem', height: '18rem', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
                pointerEvents: 'none', animation: 'sh-drift 15s ease-in-out 3s infinite',
            }} />

            <div className="container" style={{ padding: '3.5rem 1rem', position: 'relative', zIndex: 1 }}>

                {/* ── Logged-in welcome block ── */}
                {mounted && isLoggedIn && (
                    <div style={{ maxWidth: '44rem', margin: '0 auto 0' }}>
                        <div
                            className="sh-welcome-banner flex align-items-center gap-3 border-round-xl px-4 py-3 mb-3"
                            style={{
                                background: 'rgba(27,184,167,0.12)',
                                border: '1px solid rgba(27,184,167,0.32)',
                                backdropFilter: 'blur(8px)',
                            }}
                        >
                            <div
                                className="sh-avatar-circle flex align-items-center justify-content-center border-circle flex-shrink-0 font-bold"
                                style={{
                                    width: 44, height: 44,
                                    background: 'linear-gradient(135deg, #1bb8a7, #6366f1)',
                                    color: '#fff',
                                    fontSize: '1rem',
                                    letterSpacing: '-0.5px',
                                }}
                            >
                                {getInitials(authUser)}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 700, color: '#5eead4', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1 }}>
                                    Welcome back
                                </p>
                                <p style={{ margin: '3px 0 0', fontSize: '0.95rem', fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
                                    Hello {getDisplayName(authUser)}, how may we help you? <span className="sh-wave-emoji">👋</span>
                                </p>
                            </div>

                            <Button
                                label="My Orders"
                                icon="pi pi-shopping-bag"
                                className="p-button-sm flex-shrink-0"
                                style={{
                                    background: 'linear-gradient(135deg, #1bb8a7, #0ea5e9)',
                                    border: 'none',
                                    borderRadius: '0.6rem',
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    padding: '0.42rem 0.8rem',
                                    boxShadow: '0 2px 12px rgba(27,184,167,0.4)',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => router.push('/account/orders')}
                            />
                        </div>

                        <OrderTicketInfoPanel />
                    </div>
                )}

                {/* ── Main heading ── */}
                <div className="text-center">
                    <div style={{ position: 'relative', width: '5rem', height: '5rem', margin: '0 auto 1.5rem' }}>
                        <div className="sh-pulse-ring" />
                        <div className="sh-pulse-ring-2" />
                        <div
                            className="flex align-items-center justify-content-center border-circle"
                            style={{
                                width: '5rem', height: '5rem',
                                background: 'linear-gradient(135deg, rgba(27,184,167,0.3), rgba(99,102,241,0.3))',
                                border: '1.5px solid rgba(27,184,167,0.5)',
                                backdropFilter: 'blur(8px)',
                                position: 'relative', zIndex: 1,
                            }}
                        >
                            <i className="pi pi-headphones" style={{ fontSize: '2rem', color: '#5eead4' }} />
                        </div>
                    </div>

                    <h1
                        className="mt-0 mb-2"
                        style={{
                            fontSize: 'clamp(1.7rem, 4vw, 2.6rem)',
                            fontWeight: 900,
                            color: '#ffffff',
                            lineHeight: 1.2,
                            letterSpacing: '-0.5px',
                        }}
                    >
                        Help &amp; Support Center
                    </h1>

                    <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: '36rem', margin: '0 auto 2rem' }}>
                        Browse topics, find answers instantly, or reach our team directly.
                    </p>

                    {/* Quick contact pills */}
                    <div className="flex flex-wrap justify-content-center gap-3">
                        <a
                            href={contacts.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex align-items-center gap-2 no-underline border-round-3xl px-4 py-2 font-semibold"
                            style={{
                                background: 'rgba(34,197,94,0.12)',
                                border: '1.5px solid rgba(34,197,94,0.4)',
                                color: '#4ade80',
                                fontSize: '0.85rem',
                            }}
                        >
                            <i className="pi pi-whatsapp" style={{ fontSize: '1rem' }} />
                            Chat on WhatsApp
                        </a>

                        <a
                            href={`mailto:${contacts.emailSupport}`}
                            className="flex align-items-center gap-2 no-underline border-round-3xl px-4 py-2 font-semibold"
                            style={{
                                background: 'rgba(99,102,241,0.12)',
                                border: '1.5px solid rgba(99,102,241,0.4)',
                                color: '#a5b4fc',
                                fontSize: '0.85rem',
                            }}
                        >
                            <i className="pi pi-envelope" style={{ fontSize: '1rem' }} />
                            {contacts.emailSupport}
                        </a>

                        <a
                            href={`tel:${contacts.support}`}
                            className="flex align-items-center gap-2 no-underline border-round-3xl px-4 py-2 font-semibold"
                            style={{
                                background: 'rgba(27,184,167,0.12)',
                                border: '1.5px solid rgba(27,184,167,0.4)',
                                color: '#5eead4',
                                fontSize: '0.85rem',
                            }}
                        >
                            <i className="pi pi-phone" style={{ fontSize: '1rem' }} />
                            {contacts.support}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};
